// context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { sessionManager } from "../utils/sessionManager"; // ✅ Import session manager
import { setClientIconUrl } from "../utils/clientIcon";
import { clearProfileCache } from "../hooks/useProfile";
import { CANONICAL_PAGES, normalizeModuleKey, moduleKeyEquals } from "../config/moduleKeys";

let refreshPromise = null;

const isDev = process.env.NODE_ENV !== 'production';

const AuthContext = createContext();
const BASE_URL = process.env.REACT_APP_BASE_URL;

const normalizeAdminLevelData = (adminLevelData) => {
  if (!adminLevelData || typeof adminLevelData !== "object") {
    return { modules: [], features: {}, pages: null, hiddenColumns: null };
  }

  const normalizeKey = (value) => String(value || "").trim().toLowerCase();
  const normalizeFeatureValue = (value) => {
    const normalizeItem = (item) => String(item || "").trim().toLowerCase();

    if (Array.isArray(value)) {
      return value.map(normalizeItem).filter(Boolean);
    }
    if (value && typeof value === "object") {
      if (Array.isArray(value.features)) {
        return value.features.map(normalizeItem).filter(Boolean);
      }
      return Object.entries(value)
        .filter(([, enabled]) => enabled)
        .map(([featureName]) => normalizeItem(featureName))
        .filter(Boolean);
    }
    if (typeof value === "string") {
      return [normalizeItem(value)];
    }
    return [];
  };

  const modules = Array.isArray(adminLevelData.modules)
    ? adminLevelData.modules
        .map((item) => {
          if (typeof item === "string") return item;
          if (item?.module) return item.module;
          if (item?.moduleName) return item.moduleName;
          return null;
        })
        .filter(Boolean)
        .map(normalizeKey)
    : Array.isArray(adminLevelData.modulePermissions)
    ? adminLevelData.modulePermissions
        .filter((mp) => mp && mp.isEnabled !== false)
        .map((mp) => mp.module || mp.moduleName)
        .filter(Boolean)
        .map(normalizeKey)
    : [];

  let features = {};

  if (
    adminLevelData.features &&
    typeof adminLevelData.features === "object" &&
    !Array.isArray(adminLevelData.features)
  ) {
    features = Object.entries(adminLevelData.features).reduce((acc, [key, value]) => {
      const normalizedKey = normalizeKey(key);
      const normalizedValue = normalizeFeatureValue(value);
      if (normalizedValue.length) {
        acc[normalizedKey] = normalizedValue;
      }
      return acc;
    }, {});
  } else if (Array.isArray(adminLevelData.modulePermissions)) {
    features = adminLevelData.modulePermissions.reduce((acc, mp) => {
      if (!mp || mp.isEnabled === false) return acc;
      const moduleName = normalizeKey(mp.module || mp.moduleName);
      if (!moduleName) return acc;
      const enabledFeatures = Array.isArray(mp.featurePermissions)
        ? mp.featurePermissions
            .filter((fp) => fp && fp.isEnabled !== false)
            .map((fp) => normalizeKey(fp.featureName || fp.feature || fp.name))
            .filter(Boolean)
        : [];
      if (enabledFeatures.length) {
        acc[moduleName] = enabledFeatures;
      }
      return acc;
    }, {});
  }

  // ✅ v2 payloads: three-state `pages` map (absent key = HIDDEN) and
  // `hiddenColumns` map. Keys are normalized to canonical spellings so
  // consumers can look up with any legacy alias. v1 payloads simply lack
  // these fields — `null` signals "fall back to modules[] semantics".
  let pages = null;
  if (
    adminLevelData.pages &&
    typeof adminLevelData.pages === "object" &&
    !Array.isArray(adminLevelData.pages)
  ) {
    pages = Object.entries(adminLevelData.pages).reduce((acc, [key, state]) => {
      if (typeof state === "string" && state) {
        acc[normalizeModuleKey(key)] = state;
      }
      return acc;
    }, {});
  }

  let hiddenColumns = null;
  if (
    adminLevelData.hiddenColumns &&
    typeof adminLevelData.hiddenColumns === "object" &&
    !Array.isArray(adminLevelData.hiddenColumns)
  ) {
    hiddenColumns = Object.entries(adminLevelData.hiddenColumns).reduce(
      (acc, [pageKey, tables]) => {
        if (tables && typeof tables === "object" && !Array.isArray(tables)) {
          acc[normalizeModuleKey(pageKey)] = tables;
        }
        return acc;
      },
      {}
    );
  }

  return { modules, features, pages, hiddenColumns };
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    // ✅ Priority 1: Try to get user from session manager
    const sessionUser = sessionManager.getUser();
    if (sessionUser) {
      return sessionUser;
    }

    // ✅ Priority 2: Check localStorage (persistent)
    const localUser = localStorage.getItem("user");
    if (localUser) {
      try {
        return JSON.parse(localUser);
      } catch (e) {
        console.error('Error parsing localStorage user:', e);
      }
    }

    // ✅ Priority 3: Check sessionStorage
    const sessionStorageUser = sessionStorage.getItem("user");
    if (sessionStorageUser) {
      try {
        return JSON.parse(sessionStorageUser);
      } catch (e) {
        console.error('Error parsing sessionStorage user:', e);
      }
    }

    // ✅ Priority 4: Fallback - construct from individual localStorage items
    const token = localStorage.getItem('token');
    if (token) {
      return {
        username: localStorage.getItem('employeeName') ||
          localStorage.getItem('employeeEmail')?.split('@')[0] ||
          'User',
        email: localStorage.getItem('employeeEmail'),
        role: localStorage.getItem('role'),
      };
    }

    return null;
  });

  // ✅ Store permissions in app state (not localStorage)
  const [permissions, setPermissions] = useState(null);
  const [workingCompany, setWorkingCompany] = useState(() => {
    return localStorage.getItem("workingCompany") || null;
  });

  // ✅ Sync with session manager when user changes
  useEffect(() => {
    if (!user) {
      // Try to restore user from session manager
      const sessionUser = sessionManager.getUser();
      if (sessionUser) {
        setUser(sessionUser);
      }
    }
  }, [user]);

  // ✅ Resolve permissions when user changes or working company changes
  useEffect(() => {
    // Prefer admin level data attached to `user`, fall back to localStorage `adminLevel`
    let adminLevelData = null;
    if (user && user.adminLevel) {
      adminLevelData = user.adminLevel;
    } else {
      try {
        const raw = localStorage.getItem('adminLevel');
        if (raw) {
          adminLevelData = JSON.parse(raw);
        }
      } catch (e) {
        console.warn('Failed to parse localStorage.adminLevel:', e);
      }
    }

    if (adminLevelData && workingCompany) {
      // Normalize possible shapes from different endpoints
      const normalized = normalizeAdminLevelData(adminLevelData);
      const resolvedPermissions = {
        adminLevelId: adminLevelData.adminLevelId || adminLevelData.admin_level_id || adminLevelData.id || null,
        adminLevelName: adminLevelData.adminLevelName || adminLevelData.name || adminLevelData.admin_level_name || null,
        clientName: adminLevelData.clientName || adminLevelData.client || null,
        workingCompany: adminLevelData.workingCompany || adminLevelData.working_company || workingCompany,
        modules: normalized.modules,
        features: normalized.features,
        pages: normalized.pages,
        hiddenColumns: normalized.hiddenColumns,
        allModulesAllowed: adminLevelData.allModulesAllowed || false,
      };
      setPermissions(resolvedPermissions);
    } else {
      setPermissions(null);
    }
  }, [user, workingCompany]);

  // ✅ Enhanced login function
  const login = (username, rememberMe, additionalData = {}) => {
    const userData = {
      username,
      email: additionalData.email || localStorage.getItem('employeeEmail'),
      role: additionalData.role || localStorage.getItem('role'),
      // ✅ NEW: Include admin level and permissions from JWT
      adminLevel: additionalData.adminLevel || null,
      ...additionalData
    };

    setUser(userData);

    // ✅ Store working company when user logs in
    if (additionalData.workingCompany) {
      localStorage.setItem("workingCompany", additionalData.workingCompany);
      setWorkingCompany(additionalData.workingCompany);
    }

    if (Object.prototype.hasOwnProperty.call(additionalData, "clientIconUrl")) {
      const iconValue = additionalData.clientIconUrl;
      if (iconValue) {
        localStorage.setItem("clientIconUrl", iconValue);
      } else {
        localStorage.removeItem("clientIconUrl");
      }
    }

    // ✅ Initialize session manager with user data
    sessionManager.initData(userData);

    // Store in localStorage or sessionStorage based on rememberMe
    if (rememberMe) {
      localStorage.setItem("user", JSON.stringify(userData));
      sessionStorage.removeItem("user");
    } else {
      sessionStorage.setItem("user", JSON.stringify(userData));
      localStorage.removeItem("user");
    }

    // Symmetric counterpart to "hrms:auth:logout" — lets components that
    // never unmount across a login/logout cycle within the same SPA session
    // (e.g. the Mini Chat Dock) know a fresh user just logged in and re-fetch,
    // instead of only reacting on their own next mount.
    window.dispatchEvent(new Event("hrms:auth:login"));
  };

  // ✅ Update working company and refresh permissions
  const updateWorkingCompany = (company) => {
    localStorage.setItem("workingCompany", company);
    setWorkingCompany(company);
    // Permissions will auto-resolve based on new workingCompany
  };

  // ✅ Enhanced logout function
  const logout = () => {
    clearProfileCache();
    setUser(null);
    setPermissions(null);
    setWorkingCompany(null);

    // ✅ Clear session manager
    sessionManager.logout();
    setClientIconUrl(null);

    // Clear user data from storage. The JWT MUST go too — otherwise the
    // NotificationContext socket re-authenticates against the old session
    // (its useEffect only re-runs when `profile?.infinityCode` changes, and
    // `useProfile` doesn't reactively clear), the connection stays open,
    // the backend never sees a disconnect, and no `user:offline` is broadcast.
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("token");
    localStorage.removeItem("workingCompany");

    // ✅ Purge namespaced chat conversation caches
    // Remove any localStorage keys that start with the chat conversation prefix
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith('chat_conversations_')) {
        localStorage.removeItem(key);
      }
    }
    // Also purge from sessionStorage if any exist (defensive)
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('chat_conversations_')) {
        sessionStorage.removeItem(key);
      }
    }

    // Navigation is the caller's responsibility: every caller follows this
    // with navigate("/login") (SPA). A hard window.location redirect here
    // caused the login page to reload itself right after appearing.

    // NotificationContext already listens for this to tear down its socket
    // immediately (see its own comment on `onLogout`), but nothing was ever
    // dispatching it — the listener was dead. Also used by MiniChatContext
    // to clear cached conversations/messages right away: without this,
    // components that never unmount across a login/logout cycle (the socket
    // context, the Mini Chat Dock) keep showing the previous account's data,
    // since useProfile()'s cache only reactively updates on a component's own
    // mount, not on logout/re-login within the same SPA session.
    window.dispatchEvent(new Event("hrms:auth:logout"));
  };

  // ✅ Permission checking utilities
  const normalizeKey = (value) => String(value || "").trim().toLowerCase();

  // Admin-level permissions (page states, feature grants, hidden columns)
  // exist to control ONE thing: what a promoted account sees while browsing
  // in "Admin View". They must never leak into that same account's native
  // Manager View or User View — being promoted doesn't change what a manager
  // sees as a manager. Every check below bypasses whenever the current view
  // isn't admin, regardless of role, so a manager (or user) who was granted
  // an admin level keeps their full native view untouched and only sees
  // admin-level restrictions after explicitly switching to Admin View.
  const isAdminViewActive = () => {
    const vt = localStorage.getItem("managerViewType") || localStorage.getItem("viewType") || "";
    return vt.toLowerCase() === "admin";
  };

  const hasModuleAccess = (module) => {
    if (!user) return false;
    if (user.role === "superadmin" || user.role === "admin") return true;
    if (!isAdminViewActive()) return true;
    if (permissions?.allModulesAllowed) return true;
    const normalizedModule = normalizeKey(module);
    return (permissions?.modules || []).map(normalizeKey).includes(normalizedModule);
  };

  const hasFeatureAccess = (module, feature) => {
    if (!user) return false;
    if (user.role === "superadmin" || user.role === "admin") return true;
    if (!isAdminViewActive()) return true;
    if (permissions?.allModulesAllowed) return true;
    const normalizedModule = normalizeKey(module);
    if (!hasModuleAccess(normalizedModule)) return false;
    const moduleFeatures = permissions?.features?.[normalizedModule] || [];
    return moduleFeatures.map(normalizeKey).includes(normalizeKey(feature));
  };

  // ✅ Three-state page visibility (v2 admin-level payloads)
  //    "VISIBLE_ACCESSIBLE"   — show nav item, allow the page
  //    "VISIBLE_ACCESS_DENIED"— show nav item (locked), block the page
  //    "HIDDEN"               — hide nav item, redirect away from the page
  const getPageState = (pageKey) => {
    if (!user) return "HIDDEN";
    // Same bypass logic as hasModuleAccess
    if (user.role === "superadmin" || user.role === "admin") return "VISIBLE_ACCESSIBLE";
    if (!isAdminViewActive()) return "VISIBLE_ACCESSIBLE";
    if (permissions?.allModulesAllowed) return "VISIBLE_ACCESSIBLE";

    const normalized = normalizeModuleKey(pageKey);
    const pagesMap = permissions?.pages;
    if (pagesMap && typeof pagesMap === "object") {
      if (CANONICAL_PAGES.includes(normalized)) {
        return pagesMap[normalized] || "HIDDEN";
      }
      // Keys outside the canonical registry are not part of the three-state
      // system — keep modules[] semantics.
    }

    // v1 fallback: module present in modules[] → ACCESSIBLE, else HIDDEN.
    // Alias-aware so a cached legacy spelling (e.g. "solfa") still matches
    // a canonical lookup ("advancements") and vice versa.
    const inModules = (permissions?.modules || []).some((m) => moduleKeyEquals(m, pageKey));
    return inModules || hasModuleAccess(pageKey) ? "VISIBLE_ACCESSIBLE" : "HIDDEN";
  };

  // ✅ Feature visibility — thin wrapper over hasFeatureAccess (bypass = true)
  const isFeatureVisible = (pageKey, featureKey) => hasFeatureAccess(pageKey, featureKey);

  // ✅ Column visibility — columns are default-visible; only hidden when the
  // resolver's hiddenColumns map explicitly lists them. Bypassed roles see all.
  const isColumnVisible = (pageKey, tableKey, columnKey) => {
    if (!user) return true;
    if (user.role === "superadmin" || user.role === "admin") return true;
    if (!isAdminViewActive()) return true;
    if (permissions?.allModulesAllowed) return true;
    const hidden = permissions?.hiddenColumns?.[normalizeModuleKey(pageKey)]?.[tableKey];
    if (Array.isArray(hidden) && hidden.includes(columnKey)) return false;
    return true;
  };

  // ✅ Keep permissions fresh: re-resolve from the backend on mount and when
  // the tab regains focus/visibility, for non-bypassed logged-in users.
  // Without the mount-time run, a fresh page load renders from the cached
  // localStorage adminLevel until the next focus event — which looks like
  // "everything is empty until I switch apps and come back".
  // Throttled so rapid alt-tabbing never causes more than one request per
  // 30s; a company switch bypasses the throttle.
  const lastPermissionsFetchRef = useRef({ at: 0, company: null });
  useEffect(() => {
    const PERMISSIONS_REFRESH_MIN_INTERVAL_MS = 30000;

    const refreshResolvedPermissions = async () => {
      if (!user) return;
      // Bypassed roles never consult adminLevel data — nothing to refresh
      if (user.role === "superadmin" || user.role === "admin") return;
      if (!localStorage.getItem("token")) return;
      const company = workingCompany || localStorage.getItem("workingCompany");
      if (!company) return;

      const now = Date.now();
      const last = lastPermissionsFetchRef.current;
      if (last.company === company && now - last.at < PERMISSIONS_REFRESH_MIN_INTERVAL_MS) return;
      lastPermissionsFetchRef.current = { at: now, company };

      try {
        const response = await apiFetch(
          `${BASE_URL}/admin-levels/resolve/permissions?workingCompany=${encodeURIComponent(company)}`,
          { method: "GET" }
        );
        if (!response.ok) return;
        const data = await response.json();
        const fresh = data?.permissions;
        if (!fresh || typeof fresh !== "object") return;

        // Persist the same way the app stores adminLevel today so the
        // resolution effect above (user.adminLevel → localStorage.adminLevel)
        // and any next mount pick up the fresh payload.
        try {
          localStorage.setItem("adminLevel", JSON.stringify(fresh));
        } catch (e) {
          /* storage full/unavailable — state update below still applies */
        }

        setUser((prev) => {
          if (!prev) return prev;
          const updated = { ...prev, adminLevel: fresh };
          // Mirror into whichever storage currently holds the user blob
          try {
            if (localStorage.getItem("user")) {
              localStorage.setItem("user", JSON.stringify(updated));
            } else if (sessionStorage.getItem("user")) {
              sessionStorage.setItem("user", JSON.stringify(updated));
            }
          } catch (e) {
            /* non-fatal */
          }
          return updated;
        });
      } catch (e) {
        if (isDev) console.warn("Failed to refresh resolved permissions:", e);
      }
    };

    // Hydrate immediately on mount / when the session context becomes
    // available — a fresh page load must not wait for a focus event.
    refreshResolvedPermissions();

    const onFocus = () => refreshResolvedPermissions();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshResolvedPermissions();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, workingCompany]);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        permissions,
        workingCompany,
        updateWorkingCompany,
        hasModuleAccess,
        hasFeatureAccess,
        getPageState,
        isFeatureVisible,
        isColumnVisible,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Refresh access token using the refresh token from cookies
 * Returns new token on success (200), null on failure
 */
export async function refreshToken() {
  // 🔒 If refresh already in progress, wait for it
  if (refreshPromise) {
    if (isDev) console.log('⏳ Waiting for ongoing refresh...');
    return refreshPromise;
  }

  if (isDev) console.log('🔄 Starting token refresh...');

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/users/refresh-token`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.status === 401) {
        if (isDev) console.warn('⚠️ Refresh token invalid');
        performLogout();
        return null;
      }

      if (!response.ok) {
        if (isDev) console.error('❌ Refresh failed:', response.status);
        return null;
      }

      const data = await response.json();
      const newAccessToken = data.token;

      localStorage.setItem('token', newAccessToken);
      if (isDev) console.log('✅ Token refreshed');

      return newAccessToken;
    } catch (err) {
      if (isDev) console.error('❌ Refresh error:', err);
      performLogout();
      return null;
    } finally {
      // 🔓 Release semaphore
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}


/**
 * Perform logout without needing auth context (for use in apiFetch)
 */
function performLogout() {
  clearProfileCache();
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('workingCompany');
  sessionStorage.removeItem('user');
  sessionManager.logout();
  setClientIconUrl(null);

  // Redirect to login
  window.location.href = '/login';
}

/**
 * Fetch API with automatic token refresh on 401
 */
export async function apiFetch(url, options = {}) {
  const accessToken = localStorage.getItem("token");

  // Clone options to avoid mutating caller object
  options = { ...options };

  const isFormData = options.body instanceof FormData;

  // Build headers but don't force Content-Type for FormData (browser will add boundary)
  const headerViewType =
    (options.headers && (options.headers['x-view-type'] || options.headers['X-View-Type'])) ||
    null;
  const resolvedViewType =
    headerViewType ||
    localStorage.getItem('managerViewType') ||
    localStorage.getItem('viewType') ||
    'user';

  const baseHeaders = {
    ...options.headers,
    'x-view-type': resolvedViewType,
    Authorization: `Bearer ${accessToken}`,
  };
  // Ensure Accept-Language reflects current UI language (defaults to 'en')
  if (!baseHeaders['Accept-Language'] && !baseHeaders['accept-language']) {
    const uiLang = localStorage.getItem('language') || 'en';
    baseHeaders['Accept-Language'] = uiLang;
  }

  if (!isFormData && !baseHeaders["Content-Type"]) {
    baseHeaders["Content-Type"] = "application/json";
  }

  let response = await fetch(url, {
    ...options,
    headers: baseHeaders,
    credentials: "include",
  });

  // If 498 = token expired, try to refresh token and retry
  if (response.status === 498) {
    if (isDev) console.warn('⚠️ Access token expired, attempting refresh...');

    const newAccessToken = await refreshToken();

    // If refresh failed, performLogout() will be called by refreshToken()
    if (!newAccessToken) {
      return response; // Return 401 response
    }

    // Retry the original request with new token
    const retryHeaders = {
      ...options.headers,
      'x-view-type': resolvedViewType,
      Authorization: `Bearer ${newAccessToken}`,
    };

    if (!isFormData && !retryHeaders["Content-Type"]) {
      retryHeaders["Content-Type"] = "application/json";
    }

    response = await fetch(url, {
      ...options,
      headers: retryHeaders,
      credentials: "include",
    });
  }

  return response;
}
